import { supabase } from "@/integrations/supabase/client";
import { log } from "@/utils/debug";

export const getMatchingDatapoints = async (filterId: string, fieldId?: string) => {
  try {
    if (!fieldId) {
      log.error("Field ID is required to fetch datapoints", {});
      return [];
    }

    log.process("Fetching datapoints", { fieldId, filterId });

    // First get the field to know which dataset to use
    const { data: field, error: fieldError } = await supabase
      .from("fields")
      .select(
        `
        id,
        name,
        primary_dataset_id,
        secondary_dataset_id,
        filter:filters!fields_filter_id_fkey(
          id,
          name
        )
      `,
      )
      .eq("id", fieldId)
      .single();

    if (fieldError) {
      log.error(`Error fetching field: ${fieldError.message}`, {});
      return [];
    }

    const datasetId = field.primary_dataset_id || field.secondary_dataset_id;

    if (!datasetId) {
      log.error(`No dataset found for field: ${field.name}`, {});
      return [];
    }

    log.process("Using dataset", { datasetId, fieldName: field.name });

    // If no filter specified, use field's default filter if it exists
    if (!filterId || filterId === "none") {
      const { data: datapoints, error: datapointsError } = await supabase
        .from("datapoints")
        .select(
          `
          id,
          name,
          smart_tags (
            tag_value
          )
        `,
        )
        .eq("dataset_id", datasetId);

      if (datapointsError) {
        log.error(`Error fetching datapoints: ${datapointsError.message}`, {});
        return [];
      }

      log.process("Retrieved unfiltered datapoints", { count: datapoints.length });
      return datapoints.map((dp) => ({
        id: dp.id,
        name: dp.name,
        matches: true,
        matchedBy: dp.name,
        smart_tags: dp.smart_tags,
      }));
    }

    // Get the filtered datapoints with explicit dataset check
    const { data: filterDatapoints, error: filterError } = await supabase
      .from("filter_datapoints")
      .select(
        `
        datapoint_id,
        datapoints (
          id,
          name,
          dataset_id,
          smart_tags (
            tag_value
          )
        )
      `,
      )
      .eq("filter_id", filterId);

    if (filterError) {
      log.error(`Error fetching filter datapoints: ${filterError.message}`, {});
      return [];
    }

    // Ensure we only return datapoints from the correct dataset
    const relevantDatapoints = filterDatapoints
      .filter((fd) => fd.datapoints.dataset_id === datasetId)
      .map((fd) => ({
        id: fd.datapoints.id,
        name: fd.datapoints.name,
        matches: true,
        matchedBy: fd.datapoints.name,
        smart_tags: fd.datapoints.smart_tags,
      }));

    log.process("Found filtered datapoints", {
      count: relevantDatapoints.length,
      fieldName: field.name,
    });
    return relevantDatapoints;
  } catch (error) {
    log.error(`Error in getMatchingDatapoints: ${error instanceof Error ? error.message : String(error)}`, {});
    return [];
  }
};

export const getFieldFilterId = async (templateId: string, fieldId: string) => {
  try {
    console.log("[Filter] Getting filter ID for template:", templateId, "field:", fieldId);

    // First try to get the template_field and its associated filter
    const { data: templateField, error: tfError } = await supabase
      .from("template_fields")
      .select(
        `
        id,
        field:fields (
          id,
          name,
          filter_id
        ),
        template_field_filters (
          filter_id,
          is_secondary
        )
      `,
      )
      .eq("template_id", templateId)
      .eq("field_id", fieldId)
      .maybeSingle();

    if (tfError) {
      console.error("[Filter] Error getting template field:", tfError);
      throw tfError;
    }

    // If we found a template field with a custom filter, use that
    if (templateField && (templateField.template_field_filters?.length ?? 0) > 0) {
      const primaryFilter = templateField.template_field_filters.find((f) => !f.is_secondary);
      if (primaryFilter) {
        console.log("[Filter] Using template field custom filter:", primaryFilter.filter_id);
        return primaryFilter.filter_id;
      }
    }

    // If we found a template field but no custom filter, use the field's default filter
    if (templateField?.field?.filter_id) {
      console.log("[Filter] Using field default filter:", templateField.field.filter_id);
      return templateField.field.filter_id;
    }

    // If no template field found, get the field's default filter directly
    const { data: field, error: fieldError } = await supabase
      .from("fields")
      .select("filter_id, name")
      .eq("id", fieldId)
      .single();

    if (fieldError) {
      console.error("[Filter] Error getting field:", fieldError);
      throw fieldError;
    }

    if (field?.filter_id) {
      console.log("[Filter] Using field default filter (from direct query):", field.filter_id);
      return field.filter_id;
    }

    console.log("[Filter] No filter found for field, using null");
    return null;
  } catch (error) {
    console.error("[Filter] Error getting field filter ID:", error);
    return null;
  }
};

export const checkAffectedBranchingRules = async (
  fieldId: string,
  templateId: string,
  newFilterId: string,
): Promise<string[]> => {
  try {
    console.log("[Rules] Checking affected rules for field:", fieldId, "template:", templateId);

    // Get all rule set rules that use this field
    const { data: ruleSetRules, error: ruleSetError } = await supabase
      .from("template_field_rules")
      .select(
        `
        id,
        datapoint_id,
        rule_set:template_field_rule_sets!inner(
          id,
          name,
          template_field_id,
          template_field:template_fields!inner(
            id,
            template_id,
            field:fields(
              id,
              name
            )
          )
        )
      `,
      )
      .eq("field_id", fieldId)
      .eq("rule_set.template_field.template_id", templateId);

    if (ruleSetError) {
      console.error("[Rules] Error getting rule set rules:", ruleSetError);
      throw ruleSetError;
    }

    if (!ruleSetRules || ruleSetRules.length === 0) {
      console.log("[Rules] No rules found for field");
      return [];
    }

    // Get the datapoints available with the new filter
    const datapoints = await getMatchingDatapoints(newFilterId, fieldId);
    console.log("[Rules] Available datapoints for new filter:", datapoints.length);
    const availableIds = new Set(datapoints.map((d) => d.id));

    // Check which rules are affected
    const affectedFields = new Set<string>();

    ruleSetRules.forEach((rule) => {
      if (rule.datapoint_id && !availableIds.has(rule.datapoint_id)) {
        const fieldName = rule.rule_set.template_field.field?.name;
        if (fieldName) {
          console.log("[Rules] Field affected by filter change:", fieldName);
          affectedFields.add(fieldName);
        }
      }
    });

    return Array.from(affectedFields);
  } catch (error) {
    console.error("[Rules] Error checking affected rules:", error);
    return [];
  }
};
